// Story 2.3 — query hook for the bulletin picker on Step 5 of the
// composer wizard + the release panel on AdminPortfolioDetailPage.
//
// Reuses the existing GET /api/v1/admin/bulletin/posts endpoint
// (Story 1.30) with status=published. Client-side filters out posts
// that already have a non-null linked_dossier_id (already linked to
// another drop) — the v1 policy is one bulletin → one drop, and we
// don't want admin to see options that the backend will reject with
// DROP_BULLETIN_POST_ALREADY_LINKED.

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../../lib/api-client";

export type BulletinPickerRow = {
  id: string;
  slug: string;
  title: string;
  published_at: string;
  linked_dossier_id: string | null;
};

type BulletinPostsListResponse = {
  data: {
    items: Array<{
      id: string;
      slug: string;
      title: string;
      body_md: string;
      linked_dossier_id: string | null;
      published_at: string | null;
      created_at: string;
      updated_at: string;
      created_by: string;
    }>;
    page: number;
    page_size: number;
    has_next: boolean;
  };
};

async function fetchUnlinkedPublishedBulletinPosts(): Promise<
  BulletinPickerRow[]
> {
  // Story 1.30's max page size is 200; admin will have well under that
  // at v1 (low-cadence content surface).
  const response = await fetch(
    "/api/v1/admin/bulletin/posts?status=published&page_size=200",
    { credentials: "include" },
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    try {
      const raw = await response.json();
      if (raw?.error?.message) message = raw.error.message;
    } catch {
      // body not JSON
    }
    throw new Error(message);
  }
  const payload = (await response.json()) as BulletinPostsListResponse;
  const filtered: BulletinPickerRow[] = [];
  for (const p of payload.data.items) {
    if (p.published_at === null || p.linked_dossier_id !== null) continue;
    filtered.push({
      id: p.id,
      slug: p.slug,
      title: p.title,
      published_at: p.published_at,
      linked_dossier_id: null,
    });
  }
  return filtered;
}

export function useAdminBulletinPostsForDropPicker() {
  return useQuery({
    queryKey: ["admin-bulletin-posts-drop-picker"],
    queryFn: fetchUnlinkedPublishedBulletinPosts,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}
